<template>
  <div class="compressor-wrapper" v-if="currentLanguage == 'en'">

    <div
      class="drop-zone"
      :class="{ dragging: isDragging, 'has-file': originalImage }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      @click="!originalImage && $refs.fileInput.click()"
    >
      <template v-if="!originalImage">
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
            <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
          </svg>
        </div>
        <p class="drop-text">Drag & Drop your image here</p>
        <p class="drop-hint">or</p>
        <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose File</button>
        <p class="drop-formats">Supports JPG, PNG, WebP · Up to 20MB</p>
      </template>

      <template v-else>
        <div class="preview-area">
          <!-- Original -->
          <div class="preview-card">
            <div class="preview-label">Original</div>
            <img :src="originalImage" alt="Original" class="preview-img" />
            <div class="preview-meta">
              <span class="size-badge original-badge">{{ formatSize(originalSize) }}</span>
              <span class="dim-badge">{{ originalDims }}</span>
            </div>
          </div>

          <!-- Arrow -->
          <div class="preview-arrow">
            <svg v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
            <div v-else class="spinner"></div>
            <span class="saving-text" v-if="compressedImage && !isProcessing">
              -{{ savingPercent }}%
            </span>
          </div>

          <!-- Compressed -->
          <div class="preview-card">
            <div class="preview-label">Compressed</div>
            <img v-if="compressedImage" :src="compressedImage" alt="Compressed" class="preview-img" />
            <div v-else class="preview-placeholder">
              <span>Adjust settings &amp; compress</span>
            </div>
            <div class="preview-meta" v-if="compressedImage">
              <span class="size-badge compressed-badge">{{ formatSize(compressedSize) }}</span>
              <span class="dim-badge">{{ compressedDims }}</span>
            </div>
          </div>
        </div>

        <!-- Reset -->
        <button class="reset-btn" @click.stop="reset">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
          </svg>
          Upload New Image
        </button>
      </template>
    </div>

    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange" />

    <!-- Settings Panel -->
    <div class="settings-grid">
      <div class="settings-card">
        <div class="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
          Compression Settings
        </div>

        <label class="field-label">Target Image Quality</label>
        <div class="quality-tabs">
          <button
            v-for="q in qualityOptions"
            :key="q.value"
            class="quality-tab"
            :class="{ active: quality === q.value }"
            @click="quality = q.value; compress()"
          >
            {{ q.label }}
          </button>
        </div>

        <label class="field-label" style="margin-top:1.2rem">Output Format</label>
        <div class="select-wrap">
          <select v-model="outputFormat" @change="compress()">
            <option value="image/webp">Convert to WebP (Best for SEO)</option>
            <option value="image/jpeg">Keep as JPEG</option>
            <option value="image/png">Keep as PNG</option>
          </select>
          <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </div>

      <!-- Smart Dimensions -->
      <div class="settings-card">
        <div class="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          Smart Dimensions
        </div>

        <label class="field-label">Max Image Width (Pixels)</label>
        <input
          class="number-input"
          type="number"
          v-model.number="maxWidth"
          min="100"
          max="8000"
          step="10"
          @change="compress()"
        />
        <p class="field-hint">* Dimensions will be automatically reduced if the image is larger to maintain fast loading speeds.</p>

        <label class="field-label" style="margin-top:1rem">Maintain Aspect Ratio</label>
        <label class="toggle">
          <input type="checkbox" v-model="maintainAspect" @change="compress()" />
          <span class="toggle-track">
            <span class="toggle-thumb"></span>
          </span>
          <span class="toggle-text">{{ maintainAspect ? 'On' : 'Off' }}</span>
        </label>
      </div>
    </div>

    <!-- Download Button -->
    <div class="action-area" v-if="compressedImage">
      <button class="download-btn" @click="downloadCompressed">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        Download Compressed Image
      </button>
      <p class="saving-summary">You saved {{ formatSize(originalSize - compressedSize) }} · {{ savingPercent }}% reduction</p>
    </div>
  </div>
  <div class="compressor-wrapper" v-else>

    <div
      class="drop-zone"
      :class="{ dragging: isDragging, 'has-file': originalImage }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="handleDrop"
      @click="!originalImage && $refs.fileInput.click()"
    >
      <template v-if="!originalImage">
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
            <path d="M144 480C64.5 480 0 415.5 0 336c0-62.8 40.2-116.2 96.2-135.9c-.1-2.7-.2-5.4-.2-8.1c0-88.4 71.6-160 160-160c59.3 0 111 32.2 138.7 80.2C409.9 102 428.3 96 448 96c53 0 96 43 96 96c0 12.2-2.3 23.8-6.4 34.6C596 238.4 640 290.1 640 352c0 70.7-57.3 128-128 128H144zm79-217c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l39-39V392c0 13.3 10.7 24 24 24s24-10.7 24-24V257.9l39 39c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-80-80c-9.4-9.4-24.6-9.4-33.9 0l-80 80z"/>
          </svg>
          <p class="drop-text">اسحب وأفلت الصورة هنا</p>
          <p class="drop-hint">أو</p>
          <button class="choose-btn" @click.stop="$refs.fileInput.click()">اختر صورة</button>
          <p class="drop-formats">يدعم JPG و PNG و WebP · حتى 20MB</p>
          </div>
      </template>

      <template v-else>
        <div class="preview-area">
          <!-- Original -->
          <div class="preview-card">
            <div class="preview-label">الأصلية</div>
            <img :src="originalImage" alt="Original" class="preview-img" />
            <div class="preview-meta">
              <span class="size-badge original-badge">{{ formatSize(originalSize) }}</span>
              <span class="dim-badge">{{ originalDims }}</span>
            </div>
          </div>

          <!-- Arrow -->
          <div class="preview-arrow">
            <svg style="   transform: scaleX(-1);" v-if="!isProcessing" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"/>
              <polyline points="12 5 19 12 12 19"/>
            </svg>
            <div v-else class="spinner"></div>
            <span class="saving-text" v-if="compressedImage && !isProcessing">
              -{{ savingPercent }}%
            </span>
          </div>

          <!-- Compressed -->
          <div class="preview-card">
            <div class="preview-label">بعد الضغط</div>
            <img v-if="compressedImage" :src="compressedImage" alt="Compressed" class="preview-img" />
            <div v-else class="preview-placeholder">
              <span>قم بتعديل الإعدادات ثم اضغط الصورة</span>
            </div>
            <div class="preview-meta" v-if="compressedImage">
              <span class="size-badge compressed-badge">{{ formatSize(compressedSize) }}</span>
              <span class="dim-badge">{{ compressedDims }}</span>
            </div>
          </div>
        </div>

        <!-- Reset -->
        <button class="reset-btn" @click.stop="reset">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="1 4 1 10 7 10"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.36"/>
          </svg>
          ارفع صورة جديدة
        </button>
      </template>
    </div>

    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleFileChange" />

    <!-- Settings Panel -->
    <div class="settings-grid">
      <div class="settings-card">
        <div class="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
          إعدادات الضغط
        </div>

        <label class="field-label">جودة الصورة</label>
        <div class="quality-tabs">
          <button
            v-for="q in qualityOptions"
            :key="q.value"
            class="quality-tab"
            :class="{ active: quality === q.value }"
            @click="quality = q.value; compress()"
          >
            {{ q.label }}
          </button>
        </div>

        <label class="field-label" style="margin-top:1.2rem">صيغة الإخراج</label>
        <div class="select-wrap">
          <select v-model="outputFormat" @change="compress()">
            <option value="image/webp">تحويل إلى WebP (الأفضل للأداء)</option>
            <option value="image/jpeg">الاحتفاظ بـ JPEG</option>
            <option value="image/png">الاحتفاظ بـ PNG</option>
          </select>
          <svg class="select-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </div>

      <!-- Smart Dimensions -->
      <div class="settings-card">
        <div class="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
          أبعاد ذكية
        </div>

        <label class="field-label">أقصى عرض للصورة (بالبكسل)</label>
        <input
          class="number-input"
          type="number"
          v-model.number="maxWidth"
          min="100"
          max="8000"
          step="10"
          @change="compress()"
        />
        <p class="field-hint">* سيتم تقليل الأبعاد تلقائيًا إذا كانت الصورة كبيرة للحفاظ على سرعة التحميل</p>

        <label class="field-label" style="margin-top:1rem">الحفاظ على التناسب</label>
        <label class="toggle">
          <input type="checkbox" v-model="maintainAspect" @change="compress()" />
          <span class="toggle-track">
            <span class="toggle-thumb"></span>
          </span>
          <span class="toggle-text">{{ maintainAspect ? 'مفعل' : 'غير مفعل' }}</span>
        </label>
      </div>
    </div>

    <!-- Download Button -->
    <div class="action-area" v-if="compressedImage">
      <button class="download-btn" @click="downloadCompressed">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
        تحميل الصورة بعد الضغط
      </button>
      <p class="saving-summary">
  وفّرت {{ formatSize(originalSize - compressedSize) }} · تقليل {{ savingPercent }}%
</p>
    </div>
  </div>
</template>

<script>
import Pica from 'pica';
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'ImageCompressor',

  data() {
    return {
      isDragging: false,
      originalImage: null,
      currentLanguage: this.$store.state.currentLanguage,
      originalSize: 0,
      originalDims: '',
      originalFile: null,
      compressedImage: null,
      compressedSize: 0,
      compressedDims: '',
      isProcessing: false,

      // Settings
      quality: 0.8,
      outputFormat: 'image/webp',
      maxWidth: 1920,
      maintainAspect: true,

      qualityOptions: [
        { label: '🏆 HD Quality', value: 0.92 },
        { label: '⚖️ Balanced', value: 0.75 },
        { label: '🗜️ Smallest', value: 0.4 },
      ],
    };
  },

  computed: {
    savingPercent() {
      if (!this.originalSize || !this.compressedSize) return 0;
      return Math.round((1 - this.compressedSize / this.originalSize) * 100);
    },
  },

  methods: {
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.loadFile(file);
    },
    handleFileChange(e) {
      const file = e.target.files[0];
      if (file) this.loadFile(file);
    },
    loadFile(file) {
      this.originalFile = file;
      this.originalSize = file.size;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.originalImage = e.target.result;
        const img = new Image();
        img.onload = () => {
          this.originalDims = `${img.naturalWidth} × ${img.naturalHeight}`;
          this.compress();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    async compress() {
      if (!this.originalImage) return;
      this.isProcessing = true;
      this.compressedImage = null;

      await this.$nextTick();

      try {
        const img = new Image();
        await new Promise((res) => { img.onload = res; img.src = this.originalImage; });

        let targetW = img.naturalWidth;
        let targetH = img.naturalHeight;

        if (targetW > this.maxWidth) {
          const ratio = this.maxWidth / targetW;
          targetW = this.maxWidth;
          if (this.maintainAspect) targetH = Math.round(targetH * ratio);
        }

        const src = document.createElement('canvas');
        src.width = img.naturalWidth;
        src.height = img.naturalHeight;
        src.getContext('2d').drawImage(img, 0, 0);

        const dst = document.createElement('canvas');
        dst.width = targetW;
        dst.height = targetH;

        const pica = new Pica();
        await pica.resize(src, dst, { quality: 3 });
        const blob = await pica.toBlob(dst, this.outputFormat, this.quality);

        this.compressedSize = blob.size;
        this.compressedDims = `${targetW} × ${targetH}`;
        this.compressedImage = URL.createObjectURL(blob);
        this._compressedBlob = blob;
        notifyToolSuccess('image-compressor');
      } catch (err) {
        console.error(err);
      } finally {
        this.isProcessing = false;
      }
    },
    downloadCompressed() {
      if (!this._compressedBlob) return;
      const ext = this.outputFormat.split('/')[1];
      const name = (this.originalFile?.name || 'image').replace(/\.[^.]+$/, '') + `-compressed.${ext}`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(this._compressedBlob);
      a.download = name;
      a.click();
    },
    reset() {
      this.originalImage = null;
      this.compressedImage = null;
      this.originalFile = null;
      this._compressedBlob = null;
      this.$refs.fileInput.value = '';
    },
    formatSize(bytes) {
      if (!bytes) return '—';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    },
  },
    watch: {
    '$store.state.currentLanguage': function(newLanguage) {
      this.currentLanguage = newLanguage;
    }
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', sans-serif;
  max-width: 960px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper {
  direction: rtl;
  text-align: right;
}

/* ── Header ── */
.header { text-align: center; margin-bottom: 2rem; }
.title {
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 700;
  color: #3a7afe;
  margin: 0 0 .4rem;
  letter-spacing: -.02em;
}
.title em { font-style: italic; }
.subtitle { color: #6b7280; font-size: .95rem; margin: 0; }

/* ── Drop Zone ── */
.drop-zone {
  background: white;
  border: 2.5px dashed #3a7afe;
  border-radius: 20px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color .2s, background .2s;
  margin-bottom: 1.5rem;
}
.drop-zone.dragging {
  border-color: #3a7afe;
  background: #eef0ff;
}
.drop-zone.has-file { cursor: default; padding: 1.5rem; }

.drop-icon svg {
  width: 52px; height: 52px;
  fill: #3a7afe;
  margin-bottom: 1rem;
  opacity: .85;
}
.drop-text { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 .35rem; }
.drop-hint { color: #9ca3af; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #9ca3af; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe;
  color: white;
  border: none;
  border-radius: 10px;
  padding: .6rem 1.6rem;
  font-family: inherit;
  font-size: .95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #2e32d4; transform: translateY(-1px); }

/* ── Preview Area ── */
.preview-area {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}
.preview-card {
  flex: 1;
  min-width: 180px;
  max-width: 320px;
  background: #f9fafb;
  border-radius: 14px;
  overflow: hidden;
  border: 1.5px solid #e5e7eb;
}
.preview-label {
  font-size: .75rem;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #6b7280;
  padding: .5rem .8rem;
  border-bottom: 1px solid #e5e7eb;
  background: white;
}
.preview-img {
  width: 100%;
  height: 160px;
  object-fit: contain;
  background: #f3f4f6;
  display: block;
}
.preview-placeholder {
  width: 100%;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: .85rem;
  background: #f9fafb;
}
.preview-meta {
  display: flex;
  gap: .5rem;
  padding: .5rem .8rem;
  align-items: center;
  background: white;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
}
.size-badge, .dim-badge {
  font-family: 'DM Mono', monospace;
  font-size: .75rem;
  border-radius: 6px;
  padding: .2rem .5rem;
}
.original-badge { background: #fee2e2; color: #b91c1c; }
.compressed-badge { background: #dcfce7; color: #15803d; }
.dim-badge { background: #f3f4f6; color: #4b5563; }

.preview-arrow {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .4rem;
  color: #3a7afe;
}
.preview-arrow svg { width: 28px; height: 28px; }
.saving-text {
  font-size: .8rem;
  font-weight: 700;
  color: #15803d;
  background: #dcfce7;
  padding: .15rem .5rem;
  border-radius: 6px;
  font-family: 'DM Mono', monospace;
}

.spinner {
  width: 24px; height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: #3a7afe;
  border-radius: 50%;
  animation: spin .7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.reset-btn {
  display: flex;
  align-items: center;
  gap: .4rem;
  margin: 1.2rem auto 0;
  background: none;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: .4rem 1rem;
  font-family: inherit;
  font-size: .85rem;
  color: #6b7280;
  cursor: pointer;
  transition: border-color .2s, color .2s;
}
.reset-btn svg { width: 14px; height: 14px; }
.reset-btn:hover { border-color: #3a7afe; color: #3a7afe; }

/* ── Settings Grid ── */
.settings-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  margin-bottom: 1.5rem;
}
@media (max-width: 600px) { .settings-grid { grid-template-columns: 1fr; } }

.settings-card {
  background: white;
  border-radius: 16px;
  padding: 1.4rem;
  border: 1.5px solid #e5e7eb;
}
.card-title {
  display: flex;
  align-items: center;
  gap: .5rem;
  font-weight: 700;
  font-size: 1rem;
  color: #111827;
  margin-bottom: 1.1rem;
}
.card-title svg { width: 18px; height: 18px; color: #3a7afe; }

.field-label {
  display: block;
  font-size: .8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: .06em;
  margin-bottom: .5rem;
}
.field-hint {
  font-size: .75rem;
  color: #9ca3af;
  margin: .5rem 0 0;
  line-height: 1.4;
}

/* Quality Tabs */
.quality-tabs { display: flex; gap: .5rem; flex-wrap: wrap; }
.quality-tab {
  flex: 1;
  min-width: 80px;
  padding: .45rem .6rem;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-family: inherit;
  font-size: .8rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all .15s;
  text-align: center;
}
.quality-tab:hover { border-color: #3a7afe; color: #3a7afe; }
.quality-tab.active {
  background: #3a7afe;
  border-color: #3a7afe;
  color: white;
}

/* Select */
.select-wrap { position: relative; }
.select-wrap select {
  width: 100%;
  appearance: none;
  background: #f9fafb;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: .6rem 2.2rem .6rem .9rem;
  font-family: inherit;
  font-size: .9rem;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: border-color .2s;
}
.select-wrap select:focus { border-color: #3a7afe; }
.select-arrow {
  position: absolute;
  right: .7rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px; height: 16px;
  color: #6b7280;
  pointer-events: none;
}

/* Number Input */
.number-input {
  width: 100%;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: .6rem .9rem;
  font-family: 'DM Mono', monospace;
  font-size: .95rem;
  color: #374151;
  background: #f9fafb;
  outline: none;
  transition: border-color .2s;
}
.number-input:focus { border-color: #3a7afe; }

/* Toggle */
.toggle {
  display: flex;
  align-items: center;
  gap: .6rem;
  cursor: pointer;
}
.toggle input { display: none; }
.toggle-track {
  width: 40px; height: 22px;
  background: #e5e7eb;
  border-radius: 99px;
  position: relative;
  transition: background .2s;
}
.toggle input:checked + .toggle-track { background: #3a7afe; }
.toggle-thumb {
  position: absolute;
  top: 3px; left: 3px;
  width: 16px; height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform .2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.2);
}
.toggle input:checked + .toggle-track .toggle-thumb { transform: translateX(18px); }
.toggle-text { font-size: .9rem; color: #374151; font-weight: 500; }

/* ── Action Area ── */
.action-area { text-align: center; }
.download-btn {
  display: inline-flex;
  align-items: center;
  gap: .6rem;
  background: #3a7afe;
  color: white;
  border: none;
  border-radius: 12px;
  padding: .85rem 2.2rem;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: background .2s, transform .1s;
  box-shadow: 0 4px 16px rgba(59,63,232,.3);
}
.download-btn svg { width: 18px; height: 18px; }
.download-btn:hover { background: #2e32d4; transform: translateY(-2px); }
.saving-summary {
  margin: .7rem 0 0;
  color: #6b7280;
  font-size: .85rem;
}
</style>
